// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useSplitOrderObjectMapper from "@store/Framework/Common/Hooks/ReviewAndSubmit/useSplitOrderObjectMapper";
import useTrackLink from "@store/Framework/GoogleAnalytics/hooks/useTrackLink";
import CheckoutSplitOrderNotes from "@store/Library/Components/CheckoutReviewAndSubmit/CheckoutSplitOrderNotes";
import CopyButton, { CopyButtonPresentationProps } from "@store/Library/Components/CopyButton";
import ProductLine, { ProductLinePresentationProps } from "@store/Library/Components/ProductLine/ProductLine";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";
import Loader from "@store/Library/Components/Toolkit/Loader";
import { infoProps } from "@store/Themes/common/props/info";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useMemo } from "react";
import { css } from "styled-components";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import tryJsonParse from "@store/Framework/Common/Utilities/tryJsonParse";
import { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { orderV2Props } from "@store/Themes/common/props/orderV2";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { Cart } from "@insite/client-framework/Services/CartService";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import ExternalLink from "@store/Library/Components/Toolkit/Icons/ExternalLink";
import StatusBadge, { StatusBadgePresentationProps } from "@store/Library/Components/StatusBadge";
import { orderStatusColorMap } from "@store/Library/Components/statusColorMaps";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import attachToLastWord from "@store/Framework/Common/Utilities/attachToLastWord";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import constants from "@store/constants";

export interface CheckoutReviewAndSubmitSplitOrdersProps {
    cart?: Cart;
    orderNotesMaxLength?: number;
    contactLink?: string;
    showStatus?: boolean;
    showLineNumber?: boolean;
    showNotes?: boolean;
}

export interface CheckoutReviewAndSubmitSplitOrdersStyles {
    infoWrapper?: InjectableCss;
    infoTitle?: TypographyProps;
    infoValue?: TypographyProps;
    orderWrapper?: InjectableCss;
    orderTitleWrapper?: TypographyPresentationProps;
    orderTitleHeading?: TypographyPresentationProps;
    orderTitleCopyButton?: CopyButtonPresentationProps;
    orderInfosContainer?: GridContainerProps;
    orderInfoItem?: GridItemProps;
    orderNotesItem?: GridItemProps;
    orderCardWrapper?: InjectableCss;
    orderLinesWrapper?: InjectableCss;
    orderNotesWrapper?: InjectableCss;
    orderSummaryContainer?: GridContainerProps;
    productLine?: ProductLinePresentationProps;
    summaryInfosItem?: GridItemProps;
    summaryTotalValue?: TypographyProps;
    contactUsLink?: LinkPresentationProps;
    statusBadge?: StatusBadgePresentationProps;
}

export const productListStyles: CheckoutReviewAndSubmitSplitOrdersStyles = {
    infoWrapper: orderV2Props.infoWrapper,
    infoTitle: orderV2Props.infoLabel,
    infoValue: orderV2Props.infoValue,
    orderWrapper: orderV2Props.sectionWrapper,
    orderTitleWrapper: mergeProps(orderV2Props.sectionTitle, {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    }),
    orderTitleHeading: {
        css: css`
            ${inlineSeparatorStyles({ color: "text", spacing: pxToRem(21) })}
        `,
    },
    orderTitleCopyButton: {
        extendedStyles: {
            button: {
                css: css`
                    margin-left: ${pxToRem(10)};
                    vertical-align: middle;
                `,
            },
            icon: {
                size: 24,
            },
        },
    },
    orderInfosContainer: {
        gap: 24,
        css: css`
            padding: ${pxToRem(16)} ${pxToRem(24)};
            background-color: ${getColor("common.accent")};
        `,
    },
    orderInfoItem: {
        width: [12, 6, 4, 3, 3],
    },
    orderNotesItem: {
        width: 12,
    },
    orderCardWrapper: {
        css: css`
            width: 100%;
            background-color: ${getColor("common.background")};
            border: 1px solid ${getColor("common.borderLight")};
            border-radius: ${pxToRem(8)};
            overflow: hidden;
        `,
    },
    orderLinesWrapper: {
        css: css`
            width: 100%;
        `,
    },
    orderNotesWrapper: {
        css: css`
            width: 100%;
            background-color: ${getColor("common.accent")};
            border-top: 1px solid ${getColor("common.borderLight")};
        `,
    },
    productLine: {
        extendedStyles: {
            wrapper: {
                css: css`
                    border: none;
                    border-top: 1px solid ${getColor("common.borderLight")};
                    border-radius: 0;
                `,
            },
        },
    },
    orderSummaryContainer: {
        gap: 24,
        css: css`
            padding: ${pxToRem(16)} ${pxToRem(24)};
            border-top: 1px solid ${getColor("common.borderLight")};
        `,
    },
    summaryInfosItem: {
        width: [6, 6, 6, 3, 3],
    },
    summaryTotalValue: mergeProps<TypographyProps>(infoProps.value, {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    }),
    contactUsLink: linkGetActionLinkProps(ExternalLink, "right"),
};

const styles = productListStyles;

const CheckoutReviewAndSubmitSplitOrders = ({
    cart,
    orderNotesMaxLength,
    contactLink,
    showStatus = false,
    showLineNumber = false,
    showNotes = false,
}: CheckoutReviewAndSubmitSplitOrdersProps) => {
    const language = useAppSelector(state => state.context.session.language);
    const isConfirmationPage = useCurrentPage().type === constants.pageType.orderConfirmation;

    const trackTermsOfSaleLink = useTrackLink();

    const parsedOrders = useMemo(() => {
        if (cart) {
            return tryJsonParse(cart.properties.splitOrders) ?? [];
        }
        return [];
    }, [cart?.properties.splitOrders]);

    const handleTrackTermsOfSale = () => {
        trackTermsOfSaleLink({ link_clicked: "Terms Of Sale" });
    };

    if (!cart || !cart.cartLines) {
        return (
            <StyledWrapper {...styles.orderWrapper}>
                <Loader />
            </StyledWrapper>
        );
    }

    const { splitOrders } = cart.properties;
    const fakeCarts = useSplitOrderObjectMapper(splitOrders, cart);

    return fakeCarts.map((cart, index) => {
        const parsedOrder = parsedOrders[index];

        if (!parsedOrder) {
            return null;
        }

        const deliveryDateJson = parsedOrder.lines?.[0]?.properties?.deliveryDate;
        const requestedDate = tryJsonParse<{ requestedDate?: string }>(deliveryDateJson)?.requestedDate;
        const termsLink = parsedOrder.termsOfSaleDocumentLink ?? "";
        const customerPo = parsedOrder.customerPo ?? "";

        return (
            <StyledWrapper key={`${cart.id}_${index}`} {...styles.orderWrapper}>
                <Typography {...styles.orderTitleWrapper}>
                    <Typography {...styles.orderTitleHeading}>
                        {attachToLastWord(
                            `${index + 1}. ${translate("{0}:", translate("PO Number"))} ${customerPo}`,
                            <CopyButton text={customerPo} {...styles.orderTitleCopyButton} />,
                        )}
                    </Typography>
                    {(cart.erpOrderNumber || cart.key) && (
                        <Typography {...styles.orderTitleHeading}>
                            {attachToLastWord(
                                `${cart.erpOrderNumber ? translate("{0}:", translate("Greif Order Number")) : translate("{0}:", translate("Order Number"))} ${cart.erpOrderNumber || cart.key}`,
                                <CopyButton text={cart.erpOrderNumber || cart.key} {...styles.orderTitleCopyButton} />,
                            )}
                        </Typography>
                    )}
                </Typography>
                {cart.errorMessages.length > 0 && (
                    <InfosBox type="error">
                        <StyledWrapper as="ul">
                            {cart.errorMessages.map((error, index) => (
                                <StyledWrapper as="li" key={index}>
                                    {error}
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    </InfosBox>
                )}
                {cart.warningMessages.length > 0 && (
                    <InfosBox type="warning">
                        <StyledWrapper as="ul">
                            {cart.warningMessages.map((warning, index) => (
                                <StyledWrapper as="li" key={index}>
                                    {warning}
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    </InfosBox>
                )}
                <StyledWrapper {...styles.orderCardWrapper}>
                    <GridContainer {...styles.orderInfosContainer}>
                        {showStatus && (
                            <GridItem {...styles.orderInfoItem}>
                                <StyledWrapper {...styles.infoWrapper}>
                                    <Typography {...styles.infoTitle} id={`${cart.id}-orderStatus`}>
                                        {translate("Order Status")}
                                    </Typography>
                                    <StatusBadge
                                        status={cart.status}
                                        displayText={cart.statusDisplay}
                                        colorMap={orderStatusColorMap}
                                        {...styles.statusBadge}
                                    />
                                </StyledWrapper>
                            </GridItem>
                        )}
                        <GridItem {...styles.orderInfoItem}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.infoTitle} id={`${cart.id}-plant`}>
                                    {translate("Greif Location")}
                                </Typography>
                                <Typography
                                    {...styles.infoValue}
                                    aria-labelledby={`${cart.id}-plant`}
                                    data-test-selector={`${cart.id}-plant`}
                                >
                                    {cart.plant}
                                </Typography>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.orderInfoItem}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.infoTitle} id={`${cart.id}-deliveryDate`}>
                                    {translate("Requested Date")}
                                </Typography>
                                <Typography
                                    {...styles.infoValue}
                                    aria-labelledby={`${cart.id}-deliveryDate`}
                                    data-test-selector={`${cart.id}-deliveryDate`}
                                >
                                    {requestedDate
                                        ? getLocalizedDateTime({
                                              dateTime: new Date(requestedDate),
                                              language,
                                              ignoreOffset: true,
                                          })
                                        : ""}
                                </Typography>
                            </StyledWrapper>
                        </GridItem>
                        {cart.orderCarrier.trim() && (
                            <GridItem {...styles.orderInfoItem}>
                                <StyledWrapper {...styles.infoWrapper}>
                                    <Typography {...styles.infoTitle} id={`${cart.id}-orderCarrier`}>
                                        {translate("Carrier")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoValue}
                                        aria-labelledby={`${cart.id}-orderCarrier`}
                                        data-test-selector={`${cart.id}-orderCarrier`}
                                    >
                                        {cart.orderCarrier}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                        )}
                        {cart.deliveryTerms.trim() && (
                            <GridItem {...styles.orderInfoItem}>
                                <StyledWrapper {...styles.infoWrapper}>
                                    <Typography {...styles.infoTitle} id={`${cart.id}-deliveryTerms`}>
                                        {translate("Delivery Terms")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoValue}
                                        aria-labelledby={`${cart.id}-deliveryTerms`}
                                        data-test-selector={`${cart.id}-deliveryTerms`}
                                    >
                                        {cart.deliveryTerms}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                        )}
                        <GridItem {...styles.orderInfoItem}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.infoTitle} id={`${cart.id}-paymentTerms`}>
                                    {translate("Payment Terms")}
                                </Typography>
                                <Typography
                                    {...styles.infoValue}
                                    aria-labelledby={`${cart.id}-paymentTerms`}
                                    data-test-selector={`${cart.id}-paymentTerms`}
                                >
                                    {cart.paymentTerms}
                                </Typography>
                            </StyledWrapper>
                        </GridItem>
                        {contactLink && (
                            <GridItem {...styles.orderInfoItem}>
                                <StyledWrapper {...styles.infoWrapper}>
                                    <Typography {...styles.infoTitle} id={`${cart.id}-contactUs`}>
                                        {translate("Customer Service")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoValue}
                                        aria-labelledby={`${cart.id}-contactUs`}
                                        data-test-selector={`${cart.id}-contactUs`}
                                    >
                                        <Link
                                            {...styles.contactUsLink}
                                            href={`${contactLink}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {translate("Contact Us")}
                                        </Link>
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                        )}
                        {!isConfirmationPage && !!termsLink && (
                            <GridItem {...styles.orderInfoItem}>
                                <StyledWrapper {...styles.infoWrapper}>
                                    <Link
                                        href={termsLink}
                                        target="_blank"
                                        onClick={handleTrackTermsOfSale}
                                        rel="noopener noreferrer"
                                    >
                                        {translate("Terms of Sale")}
                                    </Link>
                                </StyledWrapper>
                            </GridItem>
                        )}
                        {showNotes && cart.notes && (
                            <GridItem {...styles.orderNotesItem}>
                                <StyledWrapper {...styles.infoWrapper}>
                                    <Typography {...styles.infoTitle} id={`${cart.id}-orderNotes`}>
                                        {translate("Order Notes")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoValue}
                                        aria-labelledby={`${cart.id}-orderNotes`}
                                        data-test-selector={`${cart.id}-orderNotes`}
                                    >
                                        {cart.notes}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                        )}
                    </GridContainer>
                    <StyledWrapper {...styles.orderLinesWrapper}>
                        {(cart.status === "Cart"
                            ? cart.cartLines!.filter(cartLine => !cartLine.quoteRequired)
                            : cart.cartLines!
                        ).map(cartLine => (
                            <ProductLine
                                {...styles.productLine}
                                key={cartLine.id}
                                productLine={cartLine}
                                priceProps={{
                                    showInvalidPriceMessage: true,
                                    showSavings: true,
                                }}
                                showLineNumber={showLineNumber}
                                showProductNumber
                                showFulfillment
                                showPrice
                                showQuantity
                                showSubtotal
                                showTitle
                            />
                        ))}
                    </StyledWrapper>
                    <GridContainer {...styles.orderSummaryContainer}>
                        <GridItem {...styles.summaryInfosItem}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.infoTitle} id={`${cart.id}-orderSubtotal`}>
                                    {translate("Order Subtotal")}
                                </Typography>
                                <Typography
                                    {...styles.infoValue}
                                    aria-labelledby={`${cart.id}-orderSubtotal`}
                                    data-test-selector={`${cart.id}-orderSubtotal`}
                                >
                                    {cart.orderSubTotalDisplay}
                                </Typography>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.summaryInfosItem}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.infoTitle} id={`${cart.id}-shippingHandling`}>
                                    {translate("Shipping & Handling")}
                                </Typography>
                                <Typography
                                    {...styles.infoValue}
                                    aria-labelledby={`${cart.id}-shippingHandling`}
                                    data-test-selector={`${cart.id}-shippingHandling`}
                                >
                                    {translate("TBD")}
                                </Typography>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.summaryInfosItem}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.infoTitle} id={`${cart.id}-estimatedTax`}>
                                    {translate("Estimated Tax")}
                                </Typography>
                                <Typography
                                    {...styles.infoValue}
                                    aria-labelledby={`${cart.id}-estimatedTax`}
                                    data-test-selector={`${cart.id}-estimatedTax`}
                                >
                                    {cart.totalTaxDisplay}
                                </Typography>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.summaryInfosItem}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.infoTitle} id={`${cart.id}-orderGrandTotal`}>
                                    {translate("Total")}
                                </Typography>
                                <Typography
                                    {...styles.summaryTotalValue}
                                    aria-labelledby={`${cart.id}-orderGrandTotal`}
                                    data-test-selector={`${cart.id}-orderGrandTotal`}
                                >
                                    {cart.orderGrandTotalDisplay}
                                </Typography>
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                    {orderNotesMaxLength && (
                        <StyledWrapper {...styles.orderNotesWrapper}>
                            <CheckoutSplitOrderNotes splitOrder={cart} maxLength={orderNotesMaxLength} />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    });
};

export default CheckoutReviewAndSubmitSplitOrders;
